import Color from 'color';
import styled, {css} from 'styled-components';

import ArrowNextSVG from '../../../../assets/icons/arrow-next.svg';
import GroupSVG from '../../../../assets/icons/secondary/3 User.svg';
import CalendarSVG from '../../../../assets/icons/secondary/Calendar.svg';
import DeleteSVG from '../../../../assets/icons/secondary/Delete.svg';
import ClockSVG from '../../../../assets/icons/secondary/Time Circle.svg';
import {getColor, getRem, ms, square} from '../../../../styles';
import {Button} from '../../../../uikit/Button';
import {Clickable} from '../../../../uikit/Clickable';
import {Image} from '../../../../uikit/Image';
import {Text} from '../../../../uikit/Text';
import {View} from '../../../../uikit/View';
import {platform} from '../../../../utils/platform';

type WithArchivedProp = {
  isArchived?: boolean;
};

export const ClickableWrapper = styled(Clickable)`
  ${p =>
    p.disabled &&
    css`
      pointer-events: none;
    `};
`;

export const Container = styled(View)`
  flex-direction: column;
  background-color: ${getColor(platform.isWeb ? 'background1' : 'background2')};
  border-radius: ${ms(10)};
  padding-inline: ${ms(16)};

  ${platform.isWeb &&
  css`
    box-shadow: 0 ${ms(4)} ${ms(20)}
      ${p => Color(getColor('global3')(p)).alpha(0.05).toString()};
  `};

  ${platform.isNative &&
  css`
    margin-inline: ${ms(20)};
    margin-block-start: ${ms(12)};
  `};
`;

export const Row = styled(View)`
  flex-direction: row;
  padding-block: ${ms(12)};
  column-gap: ${ms(16)};
  align-items: center;

  ${platform.isWeb &&
  css`
    height: ${ms(64)};
  `};
`;

export const CalendarIcon = styled(CalendarSVG).attrs(p => ({
  fill: getColor('icon3')(p),
}))`
  ${square(24)};
`;

export const MainInfo = styled(View)`
  flex-direction: column;
  flex: 1;
  gap: ${ms(2)};

  ${platform.isWeb &&
  css`
    width: ${ms(228)};
  `};
`;

export const MainInfoRow = styled(View)`
  flex-direction: row;
  align-items: center;
  column-gap: ${ms(8)};
`;

export const Title = styled(Text).attrs({
  nativeProps: {numberOfLines: 1},
  weight: 'semibold',
})<WithArchivedProp>`
  font-size: ${getRem(16)};
  letter-spacing: ${getRem(-0.24)};

  ${p =>
    p.isArchived &&
    css`
      color: ${getColor('text3')(p)};
    `};

  ${() =>
    platform.isWeb &&
    css`
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
    `};
`;

export const Subtitle = styled(Text).attrs({color: 'text3'})``;

export const Status = styled(Text)<{isHighlighted?: boolean}>`
  letter-spacing: ${getRem(-0.24)};
  color: ${p =>
    p.isHighlighted ? getColor('global2')(p) : getColor('text3')(p)};

  ${() =>
    platform.isWeb &&
    css`
      width: ${ms(80)};
    `};
`;

export const GroupMembersInfo = styled(View)`
  flex-direction: row;
  align-items: center;
  column-gap: ${ms(12)};
`;

export const GroupIcon = styled(GroupSVG).attrs<{
  isCoachLayout: boolean;
}>(p => ({
  fill: getColor(p.isCoachLayout ? 'global1' : 'global2')(p),
}))<{
  isCoachLayout: boolean;
}>`
  ${square(24)};
`;

export const ArchiveGroupIcon = styled(GroupIcon).attrs(p => ({
  fill: getColor('icon3')(p),
}))``;

export const GroupCapacity = styled(Text).attrs<{
  isCoachLayout: boolean;
  isGray: boolean;
}>(p => ({
  color: p.isCoachLayout ? 'global1' : 'global2',
}))<{isCoachLayout: boolean; isGray: boolean}>`
  letter-spacing: ${getRem(-0.24)};

  ${p =>
    p.isGray &&
    css`
      color: ${getColor('text3')(p)};
    `};
`;

export const Space = styled(View)`
  width: ${ms(88)};
`;

export const ButtonsContainer = styled(View)`
  flex-direction: row;
  align-items: center;
  column-gap: ${ms(8)};

  ${() =>
    platform.isWeb &&
    css`
      margin-left: auto;
      padding-inline-start: ${ms(8)};
      pointer-events: all;
    `};

  ${() =>
    platform.isNative &&
    css`
      margin-block-end: ${ms(12)};
    `};
`;

export const ActionButton = styled(Button)<{isCoachLayout?: boolean}>`
  ${p =>
    platform.isWeb && p.isCoachLayout
      ? css`
          width: ${ms(128)};
        `
      : css`
          padding-inline: ${ms(28)};
        `};

  ${() =>
    platform.isNative &&
    css`
      flex: 1;
      flex-shrink: 0;
    `};

  ${p =>
    platform.isNative &&
    p.scheme === 'danger' &&
    css`
      background-color: ${getColor('background1')(p)};
    `};
`;

export const NextArrowIcon = styled(ArrowNextSVG).attrs(p => ({
  fill: getColor('icon3')(p),
}))`
  ${square(24)};
`;

export const DeleteIcon = styled(DeleteSVG).attrs(p => ({
  fill: getColor('icon3')(p),
}))`
  ${square(24)};
`;

export const ClockIcon = styled(ClockSVG).attrs(p => ({
  fill: getColor('icon3')(p),
}))`
  ${square(24)};
  margin-inline-start: ${ms(12)};
`;

export const ClientInfo = styled(View)`
  flex-direction: row;
  align-items: center;
  column-gap: ${ms(12)};
`;

export const ClientAvatar = styled(Image)`
  ${square(24)};
  border-radius: ${ms(20)};
`;

export const UpdateContainer = styled(View)`
  flex-direction: column;
  row-gap: ${ms(8)};
  padding-inline-start: ${ms(12)};
  margin-block-end: ${ms(15)};
  border-left: 2px solid ${getColor('border3')};
`;

export const UpdateDescription = styled(Text).attrs({color: 'text3'})`
  letter-spacing: ${getRem(-0.24)};
`;
